import ConfirmModal from "PFComponents/confirm/confirm_modal";
import { LoadingDots } from "PFComponents/loading_dots";
import { Typography } from "PFComponents/typography";
import { ActivitiesCollection } from "PFCore/hooks/queries/activities/query_keys";
import { useActivitiesInvalidate } from "PFCore/hooks/queries/activities/use_activities_invalidate";
import { useCurrentProfileUpdate } from "PFCore/hooks/queries/profile";
import { PureCustomValue } from "PFTypes";
import React, { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import marketplaceButtonTheme from "../../components/buttons/marketplace_button.module.scss";
import marketplaceTertiaryButtonTheme from "../../components/buttons/marketplace_tertiary_button.module.scss";
import { useMarketplaceContext } from "../../contexts/marketplace_context";
import css from "./edit_preferences_modal.module.scss";
import { PreferredSkills } from "./parts/preferred_skills";
import { RoleDuration } from "./parts/role_duration";
import { TypeCheckboxes } from "./parts/type_checkboxes";
import { useInitialValues } from "./use_initial_values";

export type PreferencesFormValues = {
  officeWork: boolean;
  remoteWork: boolean;
  hybridWork: boolean;
  projectDurationMonths: {
    min: number;
    max: number;
  };
  skills: PureCustomValue[];
};

export const EditPreferencesModal = (): JSX.Element => {
  const { t } = useTranslation(["marketplace", "translation"]);
  const { modal } = useMarketplaceContext();
  const initialValues = useInitialValues();
  const { mutateAsync: updateCurrentProfileMutation, isLoading } = useCurrentProfileUpdate();
  const { invalidateByCollection } = useActivitiesInvalidate();

  const formMethods = useForm<PreferencesFormValues>({
    defaultValues: initialValues
  });

  useEffect(() => {
    formMethods.reset(initialValues);
  }, [formMethods, initialValues]);

  const handleOk = async (values: PreferencesFormValues) => {
    await updateCurrentProfileMutation({
      preferences: {
        office_work: values.officeWork, // eslint-disable-line camelcase
        remote_work: values.remoteWork, // eslint-disable-line camelcase
        hybrid_work: values.hybridWork, // eslint-disable-line camelcase
        project_min_duration_months: values.projectDurationMonths.min, // eslint-disable-line camelcase
        project_max_duration_months: values.projectDurationMonths.max, // eslint-disable-line camelcase
        skills: values.skills.map(({ id }) => id)
      }
    });
    invalidateByCollection(ActivitiesCollection.MatchingActivities);
    invalidateByCollection(ActivitiesCollection.Interested);
    invalidateByCollection(ActivitiesCollection.Saved);
    invalidateByCollection(ActivitiesCollection.Shared);
  };

  const handleClose = () => {
    modal.setType(null);
    modal.setData(null);
  };

  return (
    <ConfirmModal
      labelOK={!isLoading ? t("translation:save") : <LoadingDots circlesEnabled circleSize="xs" />}
      handleOK={formMethods.handleSubmit(handleOk)}
      handleClose={handleClose}
      themeCancel={marketplaceTertiaryButtonTheme}
      themeOK={marketplaceButtonTheme}
      style={{ maxWidth: 800 }}
    >
      <FormProvider {...formMethods}>
        <form className={css.form}>
          <Typography variant="h4" noMargin>
            {t("marketplace:editPreferencesModal.opportunityPreferences")}
          </Typography>
          <TypeCheckboxes />
          <RoleDuration />
          <PreferredSkills />
        </form>
      </FormProvider>
    </ConfirmModal>
  );
};
